import { ActiveElement, ChartData, Chart as ChartJS, ChartType, ChartTypeRegistry } from "chart.js";
import { mix, transparentize } from "polished";
import { RefObject } from "react";

import { useResponsive } from "@sundae/react-hooks";
import { PoolFragmentFragment } from "../gql/generated/stats2.sdk";
import { INormalizedTick } from "../stores/ExchangeContext";
import { getDisplayedAssetPrice } from "./assets.utils";

const {
  theme: { colors, fontFamily },
} = require("@sundae/tailwind-config");

export const getDisplayPriceFromTick = (tick?: INormalizedTick, pool?: PoolFragmentFragment) => {
  if (!tick || !pool) return 0;

  const price = getDisplayedAssetPrice(tick.close, pool.assetA.decimals, pool.assetB.decimals);
  return price;
};

export const get24HDeltaFromNormalizedTicks = (data: INormalizedTick[]): number | null => {
  if (data?.length) {
    const yesterday = Date.now() - 24 * 60 * 60 * 1000;
    const dataFromYesterday = data.filter(({ date }) => {
      const dateInTimestamp = new Date(date).getTime();
      return dateInTimestamp >= yesterday;
    });

    const firstOpen = dataFromYesterday[0]?.open;
    const lastClose = dataFromYesterday[dataFromYesterday.length - 1]?.close;
    return (lastClose - firstOpen) / firstOpen;
  }

  return null;
};

export const createRgbaGradient = (
  startRgba: string,
  endRgba: string,
  steps: number = 5,
  opacity?: number // 0 to 1
): string[] => {
  const appliedSteps = steps < 2 ? 2 : steps;
  const gradient = [];

  for (let i = 0; i < appliedSteps; i++) {
    const weight = i / (appliedSteps - 1);
    const mixedColor = mix(weight, startRgba, endRgba);
    const mixedRgba = opacity ? transparentize(opacity, mixedColor) : mixedColor;
    gradient.push(mixedRgba);
  }

  return gradient;
};

export const highlightDoughnutLegendItemOnHover = (
  chartRef: RefObject<ChartJS<keyof ChartTypeRegistry>>,
  activeElements: ActiveElement[],
  chartData: ChartData<ChartType>
) => {
  if (activeElements.length) {
    chartRef.current?.legend?.legendItems?.forEach((legendItem, index) => {
      if (legendItem.index === activeElements[0].index) {
        legendItem.fillStyle =
          (chartData.datasets[0].borderColor as string[])?.[activeElements[0].index] ?? "";
      } else {
        legendItem.fillStyle = (chartData.datasets[0].backgroundColor as string[])?.[index] ?? "";
      }
    });
  } else {
    chartRef.current?.legend?.legendItems?.forEach((legendItem, legendIndex) => {
      legendItem.fillStyle =
        (chartData.datasets[0].backgroundColor as string[])?.[legendIndex] ?? "";
    });
  }
};

export const getChartLegendFontConfig = (
  isDark: boolean,
  responsive: ReturnType<typeof useResponsive>
) => ({
  color: isDark ? colors.gray[300] : colors.gray[700],
  font: {
    family: fontFamily.sans[0],
    size: responsive.xl ? 11 : responsive.md ? 10 : 8,
  },
});
